<div class="diff-dialog">
  <!-- Header -->
  <div class="diff-header">
    <div class="header-title">
      <mat-icon>compare_arrows</mat-icon>
      <h2>Compare Changes: {{ data.templateName }}</h2>
    </div>
    <div class="header-summary">
      <span class="summary-item added">
        <mat-icon>add</mat-icon>
        {{ changeSummary().addedLines }} lines added
      </span>
      <span class="summary-item removed">
        <mat-icon>remove</mat-icon>
        {{ changeSummary().removedLines }} lines removed
      </span>
    </div>
    <button class="close-btn" (click)="close()">
      <mat-icon>close</mat-icon>
    </button>
  </div>

  <!-- Language Tabs + Preview Actions -->
  <div class="language-row">
    <div class="language-tabs">
      <button
        *ngFor="let lang of sortedLanguageCodes"
        class="lang-tab"
        [class.active]="selectedLang() === lang"
        [class.has-changes]="languageHasChanges()[lang]"
        (click)="selectLanguage(lang)"
      >
        {{ lang.toUpperCase() }}
        <mat-icon *ngIf="languageHasChanges()[lang]" class="change-indicator">
          circle
        </mat-icon>
      </button>
    </div>

    <div class="preview-actions">
      <button class="btn-view-published" (click)="viewPublished()" matTooltip="Preview published version">
        <mat-icon>history</mat-icon>
        View Published
      </button>
      <button class="btn-view-draft" (click)="viewDraft()" matTooltip="Preview current draft">
        <mat-icon>edit_note</mat-icon>
        View Draft
      </button>
    </div>
  </div>

  <!-- Section Tabs -->
  <div class="section-tabs">
    <button
      class="section-tab"
      [class.active]="selectedSection() === 'html'"
      (click)="selectedSection.set('html')"
    >
      <mat-icon>code</mat-icon>
      HTML Diff
    </button>
    <button
      class="section-tab"
      [class.active]="selectedSection() === 'subject'"
      (click)="selectedSection.set('subject')"
    >
      <mat-icon>subject</mat-icon>
      Subject
    </button>
    <button
      class="section-tab"
      [class.active]="selectedSection() === 'variables'"
      (click)="selectedSection.set('variables')"
    >
      <mat-icon>data_object</mat-icon>
      Variables
      <span *ngIf="hasVariableChanges()" class="change-badge">!</span>
    </button>
    <button
      class="section-tab"
      [class.active]="selectedSection() === 'description'"
      (click)="selectedSection.set('description')"
    >
      <mat-icon>description</mat-icon>
      Description
    </button>
  </div>

  <!-- Diff Content -->
  <div class="diff-content">
    <!-- HTML Diff -->
    <div class="diff-section" *ngIf="selectedSection() === 'html'">
      <div class="diff-panels">
        <div class="diff-panel published">
          <div class="panel-header">
            <mat-icon>history</mat-icon>
            <span>Published Version</span>
            <small>{{ data.published.publishedAt | dfnsFormat:'MMM d, yyyy h:mm a' }}</small>
          </div>
          <div class="panel-code">
            <pre><code>{{ data.published.htmlPart[selectedLang()] || '(empty)' }}</code></pre>
          </div>
        </div>

        <div class="diff-panel draft">
          <div class="panel-header">
            <mat-icon>edit_note</mat-icon>
            <span>Current Draft</span>
          </div>
          <div class="panel-code">
            <pre><code>{{ data.draft.htmlPart[selectedLang()] || '(empty)' }}</code></pre>
          </div>
        </div>
      </div>

      <!-- Unified Diff View -->
      <div class="unified-diff">
        <div class="diff-header-row">
          <mat-icon>difference</mat-icon>
          <span>Changes (unified view)</span>
        </div>
        <div class="diff-lines">
          <div
            *ngFor="let line of htmlDiff(); let i = index"
            class="diff-line"
            [class.added]="line.type === 'added'"
            [class.removed]="line.type === 'removed'"
            [class.unchanged]="line.type === 'unchanged'"
          >
            <span class="line-indicator">
              {{ line.type === 'added' ? '+' : line.type === 'removed' ? '-' : ' ' }}
            </span>
            <span class="line-content">{{ line.content || ' ' }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Subject Diff -->
    <div class="diff-section" *ngIf="selectedSection() === 'subject'">
      <div class="subject-comparison">
        <div class="comparison-row">
          <label>Published:</label>
          <div class="subject-text">{{ data.published.subjectPart[selectedLang()] || '(empty)' }}</div>
        </div>
        <div class="comparison-row">
          <label>Draft:</label>
          <div class="subject-text">{{ data.draft.subjectPart[selectedLang()] || '(empty)' }}</div>
        </div>
        <div class="comparison-row diff-row">
          <label>Changes:</label>
          <div class="subject-diff" [innerHTML]="subjectDiff()"></div>
        </div>
      </div>
    </div>

    <!-- Variables Diff -->
    <div class="diff-section" *ngIf="selectedSection() === 'variables'">
      <div class="variables-table">
        <div class="var-header">
          <span>Variable</span>
          <span>Status</span>
          <span>Published Value</span>
          <span>Draft Value</span>
        </div>
        <div
          *ngFor="let item of getVariableComparison()"
          class="var-row"
          [class.added]="item.status === 'added'"
          [class.removed]="item.status === 'removed'"
          [class.changed]="item.status === 'changed'"
        >
          <span class="var-name">{{ item.name }}</span>
          <span class="var-status">
            <mat-icon *ngIf="item.status === 'added'">add_circle</mat-icon>
            <mat-icon *ngIf="item.status === 'removed'">remove_circle</mat-icon>
            <mat-icon *ngIf="item.status === 'changed'">edit</mat-icon>
            <mat-icon *ngIf="item.status === 'unchanged'">check_circle</mat-icon>
            {{ item.status }}
          </span>
          <pre class="var-value">{{ formatValue(item.published?.value) }}</pre>
          <pre class="var-value">{{ formatValue(item.draft?.value) }}</pre>
        </div>
        <div *ngIf="getVariableComparison().length === 0" class="no-variables">
          No variables defined
        </div>
      </div>
    </div>

    <!-- Description Diff -->
    <div class="diff-section" *ngIf="selectedSection() === 'description'">
      <div class="description-comparison">
        <div class="comparison-row">
          <label>Published:</label>
          <div class="desc-text">{{ data.published.description || '(empty)' }}</div>
        </div>
        <div class="comparison-row">
          <label>Draft:</label>
          <div class="desc-text">{{ data.draft.description || '(empty)' }}</div>
        </div>
        <div class="comparison-row diff-row">
          <label>Changes:</label>
          <div class="desc-diff" [innerHTML]="descriptionDiff()"></div>
        </div>
      </div>
    </div>
  </div>

  <!-- Footer Actions -->
  <div class="diff-footer">
    <div class="footer-info">
      <mat-icon>info</mat-icon>
      <span>Published on {{ data.published.publishedAt | dfnsFormat:'MMM d, yyyy h:mm a' }}</span>
    </div>
    <div class="footer-actions">
      <button class="btn-secondary" (click)="close()">
        Close
      </button>
      <button class="btn-danger" (click)="revertToPublished()">
        <mat-icon>undo</mat-icon>
        Revert to Published Version
      </button>
    </div>
  </div>
</div>
